// Lib
import { keys, difference, reduce, isArray, isEmpty } from 'lodash';

// Utils
import logger from '../../../logger';
import { shouldFetchAsyncResource } from './asyncResourceUtils';
import { extractErrorDetailsForAction } from '../../../errorUtil';
import { getTimestamp } from '../../../../../common/utils/timeUtil';
import { getDataExpiryTimestamp, getDataFetchedTimestamp, isAxiosResponse } from '../axiosResponseUtils';

// Selectors
import { getAsyncResourceEntityState } from './asyncResourceSelector';

// Services
import { manuallyReportError } from '../../../../analytics/rollbarService';

// Actions
import { asyncResourceFetchError, fetchedAsyncResource, fetchingAsyncResource } from './asyncResourceActions';

// Types
import { FALLBACK_ENTITY_ID, ResourceTypes } from './asyncResourceConstants';
import {
    AsyncResourceErrorArray,
    AsyncResourceErrorMap,
    AsyncResourcePromiseResult,
    AsyncResourceWrappedFunction,
} from './asyncResourceTypes';

const getResourcePromiseErrors = (response?: AsyncResourcePromiseResult): AsyncResourceErrorMap | undefined => {
    if (!response) return undefined;

    if (isAxiosResponse(response)) {
        return response.data?.errors;
    }

    return response.asyncResource?.errors;
};

/**
 * Gets the fetched time for the data from the response.
 */
const getResultFetchedTime = (response?: AsyncResourcePromiseResult): number | undefined => {
    if (!response) return undefined;

    if (isAxiosResponse(response)) {
        // Favour the 'fetchedTime' property in the response as this will allow routes to override
        // the default api header behaviour, if necessary
        if (response.data.fetchedTime) return response.data.fetchedTime;

        return getDataFetchedTimestamp(response);
    }

    return response.asyncResource?.fetchedTime;
};

/**
 * Gets the expiry time for the data from the response.
 */
const getResponseExpiry = (response?: AsyncResourcePromiseResult): number | undefined => {
    if (!response) return undefined;

    if (isAxiosResponse(response)) {
        // Favour the 'expiry' property in the response as this will allow routes to override
        // the default api header behaviour, if necessary
        if (response?.data?.expiry) return response.data.expiry;

        return getDataExpiryTimestamp(response);
    }

    return response.asyncResource?.expiry;
};

/**
 * Gets the IDs that the wrapped async function has already handled (if any).
 */
const getResponseAlreadyHandledIds = (response?: AsyncResourcePromiseResult): string[] | undefined => {
    if (isAxiosResponse(response)) return;

    return response?.asyncResource?.alreadyHandledIds;
};

const getErrorAlreadyHandledIds = (err: any): string[] | undefined => err?.asyncResource?.alreadyHandledIds;

const getResourcePromiseResult = (response?: AsyncResourcePromiseResult): any | undefined => {
    if (!response) return undefined;

    if (isAxiosResponse(response)) return response.data;

    return response;
};

const buildActionErrorArray = (errorMap?: AsyncResourceErrorMap): AsyncResourceErrorArray | undefined =>
    reduce(
        errorMap,
        (acc, error, elementId) => {
            // TODO-RESOURCE_ERROR_REVIEW Double check if this needs to happen for both boards and elements
            const errorData = error?.error ? error.error : error;

            acc.push({
                ...errorData,
                id: elementId,
            });

            return acc;
        },
        [] as object[],
    );

type IdsToFetchResponse = [shouldFetch: boolean, idsToFetch: string[]];

/**
 * Gets the IDs to fetch as an array, using the FALLBACK_ENTITY_ID if no ids are provided.
 */
const getRequestedEntityIds = (ids?: string[] | string): string[] => {
    if (!ids) return [FALLBACK_ENTITY_ID];

    if (isArray(ids)) return ids;

    return [ids];
};

/**
 * Looks at the resource state to determine whether the ID needs to be fetched or is already being fetched.
 */
const getIdsToFetch = (
    state: object,
    resourceName: ResourceTypes,
    fetchingIds: string[],
    forceFetch?: boolean,
): IdsToFetchResponse => {
    if (forceFetch) return [true, fetchingIds];

    const idsToFetch = fetchingIds?.filter((id) => {
        const resourceState = getAsyncResourceEntityState(state, resourceName, id);
        return shouldFetchAsyncResource(resourceState);
    });

    return [!isEmpty(idsToFetch), idsToFetch];
};

/**
 * Checks to see if the wrapped function has already handled the resource state as part of this request
 * and filters it out if it has.
 */
const filterResultActionIds = (resultActionIds: string[], alreadyHandledIds: string[] | undefined): string[] => {
    if (!alreadyHandledIds) return resultActionIds;

    return difference(resultActionIds, alreadyHandledIds);
};

/**
 * Wraps a promise and fires actions to track the state of the request (fetching, fetched, error).
 *
 * This will fire the following events as appropriate that can be used in reducers if desired:
 * - RESOURCE_FETCHING
 * - RESOURCE_FETCHED
 *    - Note: This will have a data property that contains the data returned by the wrapped function
 * - RESOURCE_FETCH_ERROR
 *
 * It will also only request the entity if it hasn't been fetched, or it's expired.
 */
export const asyncResource =
    (
        // The name of the "resource". This will either track the status for the resource individually
        // or group the entities provided by the ids property
        resourceName: ResourceTypes,
        // Optional - If provided the tracking of the fetching will be grouped inside the "resourceName"
        ids?: string[] | string,
        // If we should ignore whether the resource is already fetched
        forceFetch = false,
    ) =>
    // An asynchronous function to wrap resource actions around
    (wrappedFunction: AsyncResourceWrappedFunction) =>
    async (dispatch: Function, getState: Function): Promise<void> => {
        const requestedEntityIds = getRequestedEntityIds(ids);

        // Check if already fetching or if expired
        let state = getState();
        const [shouldFetch, fetchingIds] = getIdsToFetch(state, resourceName, requestedEntityIds, forceFetch);

        if (!shouldFetch || isEmpty(fetchingIds)) {
            logger.info('asyncResource: Ignoring async resource request', { resourceName, ids, forceFetch });
            return;
        }

        const fetchStartTimestamp = getTimestamp();

        dispatch(fetchingAsyncResource(resourceName, fetchingIds));

        try {
            const response = await wrappedFunction(fetchingIds);

            state = getState();

            const fetchedTime = getResultFetchedTime(response) || fetchStartTimestamp;
            const expiry = getResponseExpiry(response);

            const result = getResourcePromiseResult(response);

            const resultErrors = getResourcePromiseErrors(response);

            const alreadyHandledIds = getResponseAlreadyHandledIds(response);
            const errorIds = keys(resultErrors);
            const fetchedIds = difference(fetchingIds, errorIds);

            const fetchedActionIds = filterResultActionIds(fetchedIds, alreadyHandledIds);

            // If there's been fetched entities, dispatch the async resource fetched action
            if (!isEmpty(fetchedActionIds)) {
                dispatch(
                    fetchedAsyncResource(resourceName, fetchedActionIds, {
                        fetchedTime,
                        data: result,
                        expiry,
                    }),
                );
            }

            const errorActionIds = filterResultActionIds(errorIds, alreadyHandledIds);

            // TODO-RESOURCE_ERROR_REVIEW Ensure the error array gets filtered correctly
            const errorArray = buildActionErrorArray(resultErrors);
            if (!isEmpty(errorActionIds) && errorArray?.length) {
                dispatch(asyncResourceFetchError(resourceName, errorArray, getTimestamp(), errorActionIds));
            }
        } catch (err: unknown) {
            const alreadyHandledIds = getErrorAlreadyHandledIds(err);

            const errorActionIds = filterResultActionIds(fetchingIds, alreadyHandledIds);

            if (!isEmpty(errorActionIds)) {
                // TODO-RESOURCE_ERROR_REVIEW - This doesn't seem correct if there's no fetching IDs
                //  NOTE: The actual error object can probably be improved now that this function is more generic, too
                const errorObject = extractErrorDetailsForAction(err);
                const errorArray = errorActionIds.map((id) => ({ ...errorObject, id }));

                dispatch(
                    asyncResourceFetchError(
                        resourceName,
                        errorArray,
                        getTimestamp(),
                        errorActionIds,
                        // TODO-RESOURCE_ERROR_REVIEW - Ideally we'd like to not duplicate this data...
                        //  Not sure why it's built like that
                        errorObject,
                    ),
                );
            }

            manuallyReportError({
                errorMessage: `${resourceName} resource fetching error: ${err}`,
                error: err,
                sensitive: false,
            });

            return Promise.reject(err);
        }
    };
